import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

export const Sheet = DialogPrimitive.Root;

export const SheetTrigger = DialogPrimitive.Trigger;

export const SheetClose = DialogPrimitive.Close;

export const SheetPortal = DialogPrimitive.Portal;

export const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    contained?: boolean;
  }
>(({ className, contained, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={twMerge(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 inset-0 z-50 bg-black/40',
      contained ? 'absolute' : 'fixed',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    side?: 'right' | 'left';
    container?: HTMLElement | null;
  }
>(({ className, children, side = 'right', container, ...props }, ref) => {
  const contained = !!container;

  return (
    <SheetPortal container={container}>
      <SheetOverlay contained={contained} />
      <DialogPrimitive.Content
        ref={ref}
        className={twMerge(
          'bg-surface-1 data-[state=open]:animate-in data-[state=closed]:animate-out inset-y-0 z-50 flex w-[420px] flex-col border shadow-xl duration-200',
          contained ? 'absolute' : 'fixed',
          side === 'right' &&
            'border-l-stroke-1 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right right-0',
          side === 'left' &&
            'border-r-stroke-1 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left left-0',
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = DialogPrimitive.Content.displayName;

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={twMerge('text-text-1 text-sm font-semibold', className)} {...props} />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={twMerge('text-text-2 text-sm', className)} {...props} />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;
